import { recordGuestJourney } from "@/lib/guest/guestJourney";
import { decideAnalyticsSend } from "@/lib/analytics/event-policy";
import posthog from 'posthog-js';

export { posthog };

type AnalyticsValue = string | number | boolean | null | undefined;
type AnalyticsProperties = Record<string, AnalyticsValue>;

let lastIdentifySignature: string | null = null;
let accessType: 'guest' | 'member' | 'unknown' = 'unknown';

// PostHog runs wherever a project key is configured — which is prod ONLY
// (staging/dev have no key, so all analytics are skipped there). Gating on the
// key — not VERCEL_ENV — because NEXT_PUBLIC_VERCEL_ENV does not reliably
// inline at build on the prod Vercel build, which silently disabled all
// browser analytics.
function isTrackingEnv(): boolean {
  return Boolean(process.env.NEXT_PUBLIC_POSTHOG_KEY);
}

// Identify user when they log in. Person properties ride on the $identify call
// itself (`setOnce` → PostHog's $set_once) — NO separate $set event, which would
// be a billable event per login. Pass everything here instead of calling a
// separate setPersonProperties().
export function identifyUser(
  userId: string,
  properties?: AnalyticsProperties,
  setOnce?: AnalyticsProperties,
): void {
  // Invalid identity calls must not change subsequent guest event attribution.
  if (!userId || typeof userId !== 'string' || userId.trim().length === 0) {
    console.warn('PostHog identifyUser: Invalid userId provided', userId);
    return;
  }
  accessType = 'member';
  if (typeof window === 'undefined' || !process.env.NEXT_PUBLIC_POSTHOG_KEY || !isTrackingEnv()) {
    return;
  }

  try {
    accessType = 'member';
    const signature = `${userId}:${JSON.stringify(properties ?? {})}:${JSON.stringify(setOnce ?? {})}`;
    if (lastIdentifySignature === signature) {
      return;
    }
    // Cache the signature only after a successful identify — otherwise a throw
    // here would leave the signature set and skip a valid retry as a duplicate.
    posthog.identify(userId, properties, setOnce);
    lastIdentifySignature = signature;
  } catch (error) {
    console.error('PostHog identifyUser error:', error);
  }
}

// Reset user when they log out
export function resetUser(): void {
  const wasMember = accessType === 'member' || lastIdentifySignature !== null;
  accessType = 'guest';
  lastIdentifySignature = null;
  if (typeof window === 'undefined' || !process.env.NEXT_PUBLIC_POSTHOG_KEY || !isTrackingEnv()) {
    return;
  }

  try {
    // Use the SDK's public persisted-user getter, not _isIdentified(). Next's
    // instrumentation initializes PostHog before the auth bootstrap runs.
    // An anonymous reload must retain its ID, but a member whose auth session
    // expired must still be detached even in a fresh JS runtime.
    let mustReset = wasMember;
    try {
      mustReset ||= Boolean(posthog.get_property('$user_id'));
    } catch {
      mustReset = true; // Unreadable identity: prefer privacy over continuity.
    }
    if (mustReset) posthog.reset();
  } catch (error) {
    console.error('PostHog resetUser error:', error);
  }
}

// Replay is disabled globally at init (major ingest cost). These helpers scope
// it to public SEO quiz pages and the short campaign-attributed signup landing
// window, then stop it before the visitor enters the wider app.
// posthog.startSessionRecording() flips disable_session_recording back off
// internally, so it works despite the init-time kill switch.
export function startSessionRecording(): void {
  if (typeof window === 'undefined' || !isTrackingEnv()) {
    return;
  }

  try {
    // `true` also overrides any remote sampling/linked-flag config so every
    // quiz visit records — volume here is ~tens of sessions a month.
    posthog.startSessionRecording(true);
  } catch (error) {
    console.error('PostHog startSessionRecording error:', error);
  }
}

export function stopSessionRecording(): void {
  if (typeof window === 'undefined' || !isTrackingEnv()) {
    return;
  }

  try {
    // Unconditional: the recorder lazy-loads after start, so a
    // sessionRecordingStarted() guard would skip the stop if the visitor
    // bounces before it finishes loading, leaving replay armed app-wide.
    posthog.stopSessionRecording();
  } catch (error) {
    console.error('PostHog stopSessionRecording error:', error);
  }
}

// Track custom events
// `departing`: the page is going away (pagehide), so skip the batch queue and beacon it now.
export function trackEvent(eventName: string, properties?: AnalyticsProperties, options?: { departing?: boolean }): void {
  // Analytics must never interrupt the action that triggered it.
  try {
    recordGuestJourney(eventName, properties ?? {}, String(properties?.access_type ?? properties?.access ?? accessType));
  } catch { /* the journey is best-effort */ }
  if (typeof window === 'undefined' || !process.env.NEXT_PUBLIC_POSTHOG_KEY || !isTrackingEnv()) {
    return;
  }

  try {
    const decision = decideAnalyticsSend(eventName, () => posthog.get_distinct_id(), Date.now(), properties);
    if (!decision.send) return;
    const payload = {
      ...properties,
      ...decision.extraProperties,
      access_type: properties?.access_type ?? accessType,
      event_source: 'web',
    };
    if (options?.departing) posthog.capture(eventName, payload, { send_instantly: true, transport: 'sendBeacon' });
    else posthog.capture(eventName, payload);
  } catch (error) {
    console.error('PostHog trackEvent error:', error);
  }
}

// (setPersonProperties removed — it captured a separate billable `$set` event
// per login [~22k/day]. Person properties now ride on identifyUser's $set /
// $set_once args [free], and the backend's deduped identifyUserProfile is the
// source of truth for them anyway.)
